import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useEffectOnce } from "../../hooks/useEffectOnce";
import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import useProcess from "../../hooks/useProcess";
import { useTranslation } from "react-i18next";
import { useDirection } from "../../hooks/useDirection";
import { config } from "../../Environment";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import "../PrescriptionList.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro"; // <-- import styles to be used
import { distributionService } from "../../services/distributionService";
import cookies from "js-cookie";

import LoggerContext from "../../context/LoggerProvider";
import PresListContext from "../../context/PresListProvider";
import { WSContext } from "../../App";

// for modal Preview!
import Preview from "../Preview";

import { Skeleton } from "../ui/skeleton";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuContent,
} from "../ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { LocationState } from "../../lib/types";
import {
  ChevronLeft,
  ChevronRight,
  Orbit,
} from "lucide-react";
import WorkflowPermissionDialog from "./modals/workflowPermissionModal/WorkflowPermissionDialog";
import {
  EngagementTaggingDialog,
  type EngagementTaggingResult,
} from "../runActivity/modals/EngagementTaggingDialog";
import ExpertShareWorkflowDialog from "./modals/mentorSharingModal/MentorSharingDialog";
import MyGroups from "./myGroups";
import MessageModal from "../MessageModal";
import MyActivities from "./myActivities";
import MyWorkspace from "./myWorkspace";
import OnboardingHero from "./OnboardingHero";
import { EXPERT_ROLE } from "../../lib/roles";
import CompanionBanner from "../companion/CompanionBanner";
import { useCompanion } from "../../context/CompanionProvider";
import { useSpaces } from "../../context/SpaceProvider";
import { useEngagementMetrics } from "../../hooks/useEngagementMetrics";
import { getTimeGreetingKey, getTimeGreetingEmoji } from "../../lib/companionGuide";
import { Flame } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";
import { useIsMobile } from "../../hooks/use-mobile";
import {
  useOrgAdminStatus,
  useMentorCollectionsQuery,
} from "../../hooks/usePortalData";

const Portal = () => {
  const { auth, user } = useAuth();
  const { startProcess, resumeProcess } = useProcess();
  const { isRTL } = useDirection();
  const hasBottomNav = useIsMobile(); // 768px — hides portal tabs when bottom nav is present

  const {
    refreshLists,
    prescriptions,
    setContextPrescriptions,
    publicPrescriptions,
    processes,
    expertProcesses,
  } = useContext(PresListContext);

  const location = useLocation();
  const navigate = useNavigate();
  // background shows that PREVIEW should be opened as modal on top of current page!
  let background = null;
  if (location.state && typeof location.state === "object") {
    const state = location.state as LocationState;
    if (state.background && typeof state.background === "object") {
      if ("pathname" in state.background) {
        background =
          state.background.pathname !== "/" ? state.background : null;
      }
    }
  }

  const { t } = useTranslation();

  const [currentPrescription, setCurrentPrescription] = useState("");
  const [currentProcess, setCurrentProcess] = useState("");

  const [reference, setReference] = useState(null);

  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 800px)").matches,
  );

  const [filterown, setFilterown] = useState("ALL");
  const [filterexpert, setFilterexpert] = useState("ALL");

  const [showTerminate, setShowTerminate] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showRename, setShowRename] = useState(false);

  const [presNameNew, setPresNameNew] = useState("");

  // Tagging dialog — shown before starting a new engagement from the portal
  const [showRunTaggingDialog, setShowRunTaggingDialog] = useState(false);
  const pendingRunPrescriptionId = useRef<string | null>(null);
  const pendingRunPrescriptionName = useRef<string | null>(null);

  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [showExpertShareWorkflowDialog, setShowExpertShareWorkflowDialog] =
    useState(false);
  const [messageClient, setMessageClient] = useState<any | null>(null);
  const [showRightChevron, setShowRightChevron] = useState(true);
  const [showLeftChevron, setShowLeftChevron] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { hasSpaces } = useSpaces();
  const isMentor = (user?.roles ?? []).includes(EXPERT_ROLE);
  const {
    messages: companionMessages,
    dismissMessage: companionDismiss,
    setActiveTab: setCompanionTab,
    userName,
  } = useCompanion();
  const { data: engagementMetrics } = useEngagementMetrics();

  // Organization admin status and mentor collections — shared React Query hooks
  const { isOrgAdmin } = useOrgAdminStatus();
  const { data: mentorCollectionsData } = useMentorCollectionsQuery(
    isMentor || isOrgAdmin,
  );
  const mentorCollectionCount =
    mentorCollectionsData?.collections?.length ?? null;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setShowLeftChevron(scrollLeft > 10); // if user scrolled a bit
      setShowRightChevron(scrollLeft + clientWidth < scrollWidth - 10); // if not fully right
    };

    const onScroll = () => {
      window.requestAnimationFrame(handleScroll);
    };

    handleScroll(); // initial check
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const scrollToRight = (e: React.MouseEvent) => {
    e.stopPropagation();
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
  };

  const scrollToLeft = (e: React.MouseEvent) => {
    e.stopPropagation();
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: 0, behavior: "smooth" });
  };

  const filteredProcesses = useMemo(() => {
    if (filterown === "ALL") {
      return processes;
    }
    if (Array.isArray(processes)) {
      return processes?.filter((process: any) => process.status === filterown);
    }
    return [];
  }, [processes, filterown]);

  const filteredExpertProcesses = useMemo(() => {
    if (filterexpert === "ALL") {
      return expertProcesses;
    }
    if (Array.isArray(expertProcesses)) {
      return expertProcesses?.filter(
        (process: any) => process.status === filterexpert,
      );
    }
    return [];
  }, [expertProcesses, filterexpert]);

  const { Logger } = useContext(LoggerContext);

  const { listRefresh, setListRefresh } = useContext(WSContext);

  const retryFetch = useRetryFetch();

  useEffectOnce(() => {
    const handler = (e: any) => setIsMobile(e.matches);
    window.matchMedia("(max-width: 800px)").addEventListener("change", handler);

    sessionStorage.removeItem("prescriptionID");
    sessionStorage.removeItem("prescriptionHistoryID");
    sessionStorage.removeItem("runProcessID");

    refreshLists();

    // subscribe to incoming messages
    let messageUnsubscribe = distributionService
      .getMessage()
      .subscribe((msg) => {
        Logger.debug("PrescriptionList incoming message: ");
        Logger.debug(msg);

        switch (msg.command) {
        }
      });

    return () => {
      messageUnsubscribe.unsubscribe();
    };
  }, []);

  const sharePrescription = async (idxOrId: any) => {
    const prescriptionId = typeof idxOrId === 'string' ? idxOrId : prescriptions[idxOrId]?._id;
    if (!prescriptionId) return;
    if (window.isSecureContext) {
      navigator.clipboard.writeText(
        `${window.location.origin}/entry/activity?wid=${prescriptionId}`,
      );
      toast.success(t("msg_link_copied_to_clipboard"));
    } else {
      toast.error(t("msg_error_clipboard_insecure"), {
        className: "!bg-red-500 !text-white",
      });
    }
  };

  const managePermissions = async (idxOrId: any) => {
    const prescriptionId = typeof idxOrId === 'string' ? idxOrId : prescriptions[idxOrId]?._id;
    if (!prescriptionId) return;
    setCurrentPrescription(prescriptionId);
    setShowPermissionDialog(true);
  };

  const expertShareWorkflow = async (idxOrId: any) => {
    const prescriptionId = typeof idxOrId === 'string' ? idxOrId : prescriptions[idxOrId]?._id;
    if (!prescriptionId) return;
    setCurrentPrescription(prescriptionId);
    setShowExpertShareWorkflowDialog(true);
  };

  const exportPrescription = async (idxOrId: any) => {
    const pres = typeof idxOrId === 'string'
      ? prescriptions.find((p: any) => p._id === idxOrId)
      : prescriptions[idxOrId];
    const prescriptionId = pres?._id || (typeof idxOrId === 'string' ? idxOrId : undefined);
    if (!prescriptionId) return;
    let response = await retryFetch(
      `${config.API_BASE}/api/prescriptions/download/${prescriptionId}`,
    );
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const safeName = (pres?.name || prescriptionId).replace(/[^a-zA-Z0-9_\-\s]/g, '').replace(/\s+/g, '_');
    link.setAttribute(
      "download",
      `ClaryNext_${safeName}.json`,
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  };

  const runPrescription = async (prescriptions: any, idx: any) => {
    const prescriptionId = prescriptions[idx]._id;
    if (isMentor) {
      pendingRunPrescriptionId.current = prescriptionId;
      pendingRunPrescriptionName.current = prescriptions[idx].name || null;
      setShowRunTaggingDialog(true);
    } else {
      await startProcess({
        prescriptionId,
        from: location,
        forward: location,
        onError: () => { toast.error("An error occurred while starting the prescription."); },
      });
    }
  };

  const runPrescriptionById = async (prescriptionId: any, prescriptionName?: string) => {
    if (isMentor) {
      pendingRunPrescriptionId.current = prescriptionId;
      pendingRunPrescriptionName.current = prescriptionName || null;
      setShowRunTaggingDialog(true);
    } else {
      await startProcess({
        prescriptionId,
        from: location,
        forward: location,
        onError: () => { toast.error("An error occurred while starting the prescription."); },
      });
    }
  };

  const runOtherPrescription = async (idx: any) => {
    const prescriptionId = publicPrescriptions[idx]._id;
    if (isMentor) {
      pendingRunPrescriptionId.current = prescriptionId;
      pendingRunPrescriptionName.current = publicPrescriptions[idx].name || null;
      setShowRunTaggingDialog(true);
    } else {
      await startProcess({
        prescriptionId,
        from: location,
        forward: location,
        onError: () => { toast.error("An error occurred while starting the prescription."); },
      });
    }
  };

  const handleRunTaggingConfirm = async (result: EngagementTaggingResult) => {
    setShowRunTaggingDialog(false);
    const prescriptionId = pendingRunPrescriptionId.current;
    pendingRunPrescriptionId.current = null;
    pendingRunPrescriptionName.current = null;
    if (!prescriptionId) return;
    await startProcess({
      prescriptionId,
      engagementName: result.engagementName,
      tags: result.tags,
      clientIdentifier: result.clientIdentifier,
      from: location,
      forward: location,
      onError: () => {
        toast.error("An error occurred while starting the prescription.");
      },
    });
  };

  const continueProcess = async (idx: any) => {
    let processToContinue = filteredProcesses[idx]._id;
    if (filteredProcesses[idx].pendingInnerProcess) {
      Logger.debug("PENDING INNER PROCESS detected, switching to this one!");
      processToContinue = filteredProcesses[idx].pendingInnerProcess;
    }
    await resumeProcess({
      processId: processToContinue,
      from: location,
      forward: location,
      onError: () => {
        toast.error("An error occurred while resuming the prescription.");
        refreshLists();
      },
    });
  };

  const terminateProcess = async (idx: any) => {
    setCurrentProcess(filteredProcesses[idx]._id);
    setShowTerminate(true);
  };

  const showUserLog = (idx: any) => {
    Logger.debug("UserLog should be shown for " + filteredProcesses[idx]._id);
    let stateToPass = {
      processId: filteredProcesses[idx]._id,
      presName: filteredProcesses[idx].prescriptionName,
      presHistoryId: filteredProcesses[idx].fiPrescriptionHistory,
      expertEmail: filteredProcesses[idx].fiExpert?.email,
      autochat: false,
    };

    navigate("/review", { state: stateToPass });
  };

  const showFullLog = (idx: any) => {
    Logger.debug(
      "FullLog should be shown for " + filteredExpertProcesses[idx]._id,
    );
    let stateToPass = {
      processId: filteredExpertProcesses[idx]._id,
      presName: filteredExpertProcesses[idx].prescriptionName,
      presHistoryId: filteredExpertProcesses[idx].fiPrescriptionHistory,
      userEmail: filteredExpertProcesses[idx].fiInitiator?.email,
      autochat: false,
    };
    navigate("/review", { state: stateToPass });
  };

  const deletePrescription = (idxOrId: any) => {
    const pres = typeof idxOrId === 'string'
      ? prescriptions.find((p: any) => p._id === idxOrId)
      : prescriptions[idxOrId];
    if (!pres) return;
    setCurrentPrescription(pres._id);
    if (pres.versions > 0) {
      setShowArchive(true);
    } else {
      setShowDelete(true);
    }
  };

  const renamePrescription = (idxOrId: any) => {
    const pres = typeof idxOrId === 'string'
      ? prescriptions.find((p: any) => p._id === idxOrId)
      : prescriptions[idxOrId];
    if (!pres) return;
    setReference(pres._id);
    setPresNameNew(pres.name);
    setShowRename(true);
  };

  const showLocale = (ts: any) => {
    return new Date(ts).toLocaleString(
      cookies.get("i18next")?.split("-")[0] || "en",
    );
  };

  const viewPrescription = (idxOrId: any, isMine: boolean) => {
    let prescriptionId: string | undefined;
    if (isMine) {
      prescriptionId = typeof idxOrId === 'string' ? idxOrId : prescriptions[idxOrId]?._id;
    } else {
      prescriptionId = typeof idxOrId === 'string' ? idxOrId : publicPrescriptions[idxOrId]?._id;
    }
    if (!prescriptionId) return;
    sessionStorage.setItem("prescriptionID", prescriptionId);
    sessionStorage.removeItem("draftprescription");
    navigate("/readonly");
  };

  const copyPrescription = async (idx: any) => {
    let presId = publicPrescriptions[idx]._id;
    await retryFetch(
      `${config.API_BASE}/api/prescriptions/public/copy/${presId}`,
      {
        method: "POST",
        credentials: "include",
      },
    );

    toast.success(t("msg_activity_copy_successful"));

    refreshLists();
  };

  const copyPrescriptionAsExample = async (idxOrId: any) => {
    const prescriptionId = typeof idxOrId === 'string' ? idxOrId : prescriptions[idxOrId]?._id;
    if (!prescriptionId) return;
    try {
      let response = await retryFetch(
        `${config.API_BASE}/api/prescriptions/copy/${prescriptionId}`,
        {
          method: "POST",
          credentials: "include",
        },
      );

      if (!response.ok)
        throw new Error(response.status + ": " + response.statusText);

      toast.success(t("msg_activity_copy_successful"));

      refreshLists();
    } catch (e) {
      toast.error(t("msg_error_activity_copy"), {
        className: "!bg-red-500 !text-white",
      });
    }
  };

  const handleImportPrescription = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async function (e: any) {
        const content = e.target.result;
        try {
          const importData = JSON.parse(content);

          let newResponse = await retryFetch(
            config.API_BASE + "/api/prescriptions/import",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
              body: JSON.stringify(importData),
            },
          );

          let data = await newResponse.json();

          if (!data.name) throw new Error("");
          let newName = data.name;

          toast.success("Prescription was successfully imported as " + newName);

          setListRefresh(true);
        } catch (error) {
          toast.error("Error importing prescription", {
            className: "!bg-red-500 !text-white",
          });

          console.error("Error parsing JSON:", error);
        }
      };
      reader.readAsText(file);
    }
  };

  useEffect(() => {
    if (listRefresh) {
      refreshLists();
      setListRefresh(false);
    }
  }, [listRefresh]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Track sidebar state
  const [activePortalTab, setActivePortalTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    // URL ?tab= param takes priority over localStorage
    const tabFromUrl = params.get("tab");
    if (tabFromUrl) return tabFromUrl;
    // Wizard params force activities tab
    if (params.has("wizard_data") || params.has("auto_open")) return "activities";
    // Fall back to localStorage
    return localStorage.getItem("portal_active_tab") || "activities";
  });

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen); // Toggle the sidebar state
  };

  const handleTabClick = () => {
    setIsSidebarOpen(false); // Close sidebar when a tab is clicked
  };

  const handlePortalTabChange = (value: string) => {
    setActivePortalTab(value);
    localStorage.setItem("portal_active_tab", value);
    setCompanionTab(value);
    // Sync URL so the tab is shareable/bookmarkable
    navigate(`/portal?tab=${value}`, { replace: true });
  };

  // Listen for tab-change events from the global AppRail
  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent;
      handlePortalTabChange(customEvent.detail);
    };
    window.addEventListener("portal-tab-change", handler);
    return () => window.removeEventListener("portal-tab-change", handler);
  }, []);

  return (
    <div className="flex flex-col flex-1">
      <OnboardingHero />
      {isMobile ? (
        <>
          {/* Companion Banner — mobile */}
          {companionMessages.slice(0, 1).map((msg) => (
            <div key={msg.id} className="px-4 pt-2">
              <CompanionBanner message={msg} onDismiss={companionDismiss} mini />
            </div>
          ))}
          <Tabs
            className="flex flex-col flex-1"
            value={activePortalTab}
            onValueChange={handlePortalTabChange}
          >
            {/* Greeting + mobile navigation bar — hidden when bottom nav is present */}
            {!hasBottomNav && <div className="sticky z-40 bg-white/60 backdrop-blur-sm border-b border-gray-200/60">
              <div className="px-4 pt-2 pb-1">
                <p className="text-xs text-muted-foreground font-medium tracking-tight">
                  {getTimeGreetingEmoji()}{" "}{t(getTimeGreetingKey())}{userName ? `, ${userName}` : ""}
                  {engagementMetrics && engagementMetrics.currentStreak > 0 && (
                    <span className="text-muted-foreground/60 font-normal ms-1.5 inline-flex items-center gap-1">
                      <Flame className="w-3 h-3 text-orange-500" />
                      {t("day_streak", { count: engagementMetrics.currentStreak })}
                    </span>
                  )}
                </p>
              </div>
              <div className="relative">
                {showRightChevron && (
                  <div
                    onClick={scrollToRight}
                    className={`absolute top-0 ${isRTL ? "left-0" : "right-0"} z-10 flex items-center justify-center w-10 h-full bg-white ${isRTL ? "border-e" : "border-s"} border-gray-200 cursor-pointer`}
                  >
                    {isRTL ? (
                      <ChevronLeft className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                )}
                {showLeftChevron && (
                  <div
                    onClick={scrollToLeft}
                    className={`absolute top-0 ${isRTL ? "right-0" : "left-0"} z-10 flex items-center justify-center w-10 h-full bg-white ${isRTL ? "border-s" : "border-e"} border-gray-200 cursor-pointer`}
                  >
                    {isRTL ? (
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronLeft className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                )}
                <div
                  ref={scrollRef}
                  className="px-4 overflow-x-auto"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                  <TabsList className="inline-flex items-center justify-start w-auto h-auto min-w-full gap-8 p-0 bg-transparent">
                    <TabsTrigger
                      className={cn(
                        "relative whitespace-nowrap border-b-0 px-2 py-4 text-sm font-semibold transition-all rounded-none bg-transparent shadow-none",
                        activePortalTab === "activities" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                      )}
                      value="activities"
                    >
                      {t("activities")}
                      {activePortalTab === "activities" && (
                        <motion.div
                          layoutId="portal-tab-indicator-mobile"
                          className="absolute bottom-0 inset-x-0 h-0.5 bg-primary rounded-full"
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </TabsTrigger>
                    <TabsTrigger
                        value="my_workspace"
                        className={cn(
                          "relative whitespace-nowrap border-b-0 px-2 py-4 text-sm font-semibold transition-all rounded-none bg-transparent shadow-none",
                          activePortalTab === "my_workspace" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                      {t("workspace")}
                      {activePortalTab === "my_workspace" && (
                        <motion.div
                          layoutId="portal-tab-indicator-mobile"
                          className="absolute bottom-0 inset-x-0 h-0.5 bg-primary rounded-full"
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </TabsTrigger>

                    {(isMentor || isOrgAdmin) &&
                      mentorCollectionCount !== 0 && (
                        <TabsTrigger
                          value="my_groups"
                          className={cn(
                            "relative whitespace-nowrap border-b-0 px-2 py-4 text-sm font-semibold transition-all rounded-none bg-transparent shadow-none",
                            activePortalTab === "my_groups" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                          )}
                        >
                          {t("groups")}
                          {activePortalTab === "my_groups" && (
                            <motion.div
                              layoutId="portal-tab-indicator-mobile"
                              className="absolute bottom-0 inset-x-0 h-0.5 bg-primary rounded-full"
                              transition={{ type: "spring", stiffness: 500, damping: 30 }}
                            />
                          )}
                        </TabsTrigger>
                      )}

                    {!hasSpaces && (
                      <button
                        onClick={() => navigate("/spaces")}
                        className="whitespace-nowrap px-2 py-4 text-sm font-medium text-gray-400 hover:text-primary transition-colors flex items-center gap-1.5"
                      >
                        <Orbit className="w-3.5 h-3.5" />
                        {t("spaces")}
                      </button>
                    )}
                  </TabsList>
                </div>
              </div>
            </div>}

            <TabsContent value="activities">
              <MyActivities
                // prescriptions={prescriptions}
                setContextPrescriptions={setContextPrescriptions}
                viewPrescription={viewPrescription}
                renamePrescription={renamePrescription}
                deletePrescription={deletePrescription}
                copyPrescriptionAsExample={copyPrescriptionAsExample}
                sharePrescription={sharePrescription}
                managePermissions={managePermissions}
                expertShareWorkflow={expertShareWorkflow}
                exportPrescription={exportPrescription}
                runPrescription={runPrescription}
                runPrescriptionById={runPrescriptionById}
                refreshLists={refreshLists}
                isMobile={true}
              />
            </TabsContent>

            <TabsContent value="my_workspace" className="h-full">
              <MyWorkspace />
            </TabsContent>
            {/*     <TabsContent value="public">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("name")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!publicPrescriptions
                    ? publicPrescriptions.map(
                        (prescription: any, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell
                                className={`${
                                  (prescription.isWelcome ? "font-bold" : "") +
                                  (!prescription.valid ? " text-gray-300" : "")
                                }`}
                              >
                                {prescription.name}
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger className="bg-transparent ms-2">
                                    <div className="w-[30px]">
                                      <FontAwesomeIcon
                                        title={t("actions")}
                                        style={{ paddingTop: "4px" }}
                                        icon={solid("ellipsis-vertical")}
                                      />
                                    </div>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem
                                      className="cursor-pointer"
                                      onClick={() =>
                                        viewPrescription(index, false)
                                      }
                                    >
                                      <div className="flex">
                                        <FontAwesomeIcon
                                          title={t("view")}
                                          icon={solid("eye")}
                                          className="ml-2 mr-2 mt-1 w-[15px]"
                                        />
                                        <span>{t("view")}</span>
                                      </div>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="cursor-pointer"
                                      onClick={() => copyPrescription(index)}
                                    >
                                      <div className="flex">
                                        <FontAwesomeIcon
                                          title={t("copy")}
                                          icon={solid("copy")}
                                          className="ml-2 mr-2 mt-1 w-[15px]"
                                        />
                                        <span>{t("copy")}</span>
                                      </div>
                                    </DropdownMenuItem>
                                    {!!prescription.valid ? (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() =>
                                            runOtherPrescription(index)
                                          }
                                        >
                                          <div className="flex">
                                            <FontAwesomeIcon
                                              title={t("run")}
                                              icon={solid("play")}
                                              className="ml-2 mr-2 mt-1 w-[15px]"
                                            />
                                            <span>{t("run")}</span>
                                          </div>
                                        </DropdownMenuItem>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[400px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent>*/}
            {/* <TabsContent value="My Engagements">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("activity_name")}</TableHead>
                    <TableHead>{t("expert")}</TableHead>
                    <TableHead>
                      {t("status")}{" "}
                      <select
                        className="filterselect"
                        value={filterown}
                        onChange={(e) => setFilterown(e.target.value)}
                      >
                        <option value="ALL">{t("ALL")}</option>
                        <option value="RUNNING">{t("RUNNING")}</option>
                      </select>
                    </TableHead>
                    <TableHead>{t("last_update")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!filteredProcesses
                    ? filteredProcesses.map((process: any, index: any) => {
                        return (
                          <TableRow key={index}>
                            <TableCell>{process.prescriptionName}</TableCell>
                            <TableCell>
                              {process.fiExpert?.email}&nbsp;
                              {process.fiExpert &&
                              process.fiExpert.email !== auth.email ? (
                                <span
                                  className={
                                    "smallicon " +
                                    (process.onlineState === "offline"
                                      ? "offline"
                                      : process.onlineState === "online"
                                      ? "online"
                                      : "busy")
                                  }
                                >
                                  <FontAwesomeIcon
                                    title={t("onlinestate")}
                                    icon={solid("circle")}
                                  />
                                </span>
                              ) : (
                                ""
                              )}
                            </TableCell>
                            <TableCell>{t(process.status)}</TableCell>
                            <TableCell>
                              {showLocale(process.lastUpdated)}
                            </TableCell>
                            <TableCell>
                              {process.status === "RUNNING" ||
                              process.logCount > 0 ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger className="bg-transparent ms-2">
                                    <div className="w-[30px]">
                                      <FontAwesomeIcon
                                        title={t("actions")}
                                        style={{ paddingTop: "4px" }}
                                        icon={solid("ellipsis-vertical")}
                                      />
                                    </div>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    {process.status === "RUNNING" ? (
                                      <>
                                        {" "}
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() => continueProcess(index)}
                                        >
                                          <div className="flex">
                                            <FontAwesomeIcon
                                              title={t("continue")}
                                              icon={solid("forward")}
                                              className="ml-2 mr-2 mt-1 w-[15px]"
                                            />
                                            <span>{t("continue")}</span>
                                          </div>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() =>
                                            terminateProcess(index)
                                          }
                                        >
                                          <div className="flex">
                                            <FontAwesomeIcon
                                              title={t("terminate")}
                                              icon={solid("circle-xmark")}
                                              className="ml-2 mr-2 mt-1 w-[15px]"
                                            />
                                            <span>{t("terminate")}</span>
                                          </div>
                                        </DropdownMenuItem>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                    {process.logCount > 0 ? (
                                      <>
                                        {process.status === "RUNNING" ? (
                                          <DropdownMenuSeparator />
                                        ) : (
                                          <></>
                                        )}
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() => showUserLog(index)}
                                        >
                                          <div className="flex">
                                            <FontAwesomeIcon
                                              title={t("review")}
                                              icon={solid("file-lines")}
                                              className="ml-2 mr-2 mt-1 w-[15px]"
                                            />
                                            <span>{t("review")}</span>
                                          </div>
                                        </DropdownMenuItem>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <></>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[200px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[20px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent> */}
            {/* <TabsContent value="Engagements For Activities">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("activity_name")}</TableHead>
                    <TableHead>{t("user")}</TableHead>
                    <TableHead>
                      {t("status")}
                      <select
                        className="filterselect"
                        value={filterexpert}
                        onChange={(e) => setFilterexpert(e.target.value)}
                      >
                        <option value="ALL">{t("ALL")}</option>
                        <option value="RUNNING">{t("RUNNING")}</option>
                      </select>
                    </TableHead>
                    <TableHead>{t("last_update")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!filteredExpertProcesses
                    ? filteredExpertProcesses.map(
                        (process: any, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell>{process.prescriptionName}</TableCell>
                              <TableCell>
                                {process.fiInitiator?.email}&nbsp;
                                {process.fiInitiator &&
                                process.fiInitiator.email !== auth.email ? (
                                  <span
                                    className={
                                      "smallicon " +
                                      (process.onlineState === "offline"
                                        ? "offline"
                                        : process.onlineState === "online"
                                        ? "online"
                                        : "busy")
                                    }
                                  >
                                    <FontAwesomeIcon
                                      title={t("onlinestate")}
                                      icon={solid("circle")}
                                    />
                                  </span>
                                ) : (
                                  ""
                                )}
                              </TableCell>
                              <TableCell>{t(process.status)}</TableCell>
                              <TableCell>
                                {showLocale(process.lastUpdated)}
                              </TableCell>
                              <TableCell>
                                {!process.fiInitiator ||
                                process.logCount > 0 ? (
                                  <Menubar>
                                    <MenubarMenu>
                                      <MenubarTrigger className="bg-transparent cursor-pointer w-[30px]">
                                        <FontAwesomeIcon
                                          title={t("actions")}
                                          style={{ paddingTop: "4px" }}
                                          icon={solid("ellipsis-vertical")}
                                        />
                                      </MenubarTrigger>
                                      <MenubarContent>
                                        {process.logCount > 0 ? (
                                          <MenubarItem
                                            className="cursor-pointer"
                                            onClick={() => showFullLog(index)}
                                          >
                                            <div className="flex">
                                              <FontAwesomeIcon
                                                title={t("review")}
                                                icon={solid("file-lines")}
                                                className="ml-2 mr-2 mt-1 w-[15px]"
                                              />
                                              <span>{t("review")}</span>
                                            </div>
                                          </MenubarItem>
                                        ) : (
                                          <></>
                                        )}
                                      </MenubarContent>
                                    </MenubarMenu>
                                  </Menubar>
                                ) : (
                                  <></>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[200px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[20px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent> */}

            {(isMentor || isOrgAdmin) &&
              mentorCollectionCount !== null &&
              mentorCollectionCount > 0 && (
                <TabsContent value="my_groups">
                  <MyGroups />
                </TabsContent>
              )}

          </Tabs>

          {/* <Table className="mt-4 preslist">
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!!validPrescriptions
                ? validPrescriptions.map((prescription: any, index: number) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>{prescription.name}</TableCell>
                        <TableCell className="text-center">
                          <FontAwesomeIcon
                            title={t("run")}
                            icon={solid("play")}
                            onClick={() =>
                              runPrescription(validPrescriptions, index)
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                : Array(Math.floor(Math.random() * 10 + 5))
                    .join(".")
                    .split(".")
                    .map((_, arrIdx) => {
                      return (
                        <TableRow key={arrIdx}>
                          <TableCell>
                            <Skeleton className="h-[20px] w-[260px] rounded-xl bg-gray-200" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                          </TableCell>
                        </TableRow>
                      );
                    })}
            </TableBody>
          </Table> */}
        </>
      ) : (
        <Tabs
          className="flex flex-1 overflow-hidden"
          value={activePortalTab}
          onValueChange={handlePortalTabChange}
        >
          <div className="flex-1 overflow-auto">
              {/* Companion Banner — desktop, inside content area so rail stays fixed */}
              {companionMessages.slice(0, 1).map((msg) => (
                <div key={msg.id} className="px-6 pt-3">
                  <CompanionBanner message={msg} onDismiss={companionDismiss} mini />
                </div>
              ))}
              <TabsContent value="activities" className="h-full mt-0">
                <MyActivities
                  // prescriptions={prescriptions}
                  setContextPrescriptions={setContextPrescriptions}
                  viewPrescription={viewPrescription}
                  renamePrescription={renamePrescription}
                  deletePrescription={deletePrescription}
                  copyPrescriptionAsExample={copyPrescriptionAsExample}
                  sharePrescription={sharePrescription}
                  managePermissions={managePermissions}
                  expertShareWorkflow={expertShareWorkflow}
                  exportPrescription={exportPrescription}
                  runPrescription={runPrescription}
                  runPrescriptionById={runPrescriptionById}
                  refreshLists={refreshLists}
                  isMobile={false}
                />
              </TabsContent>

              <TabsContent value="my_workspace" className="h-full mt-0">
                <MyWorkspace />
              </TabsContent>
              {/*  <TabsContent value="public">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("name")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!publicPrescriptions
                    ? publicPrescriptions.map(
                        (prescription: any, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell
                                className={`${
                                  (prescription.isWelcome ? "font-bold" : "") +
                                  (!prescription.valid ? " text-gray-300" : "")
                                }`}
                              >
                                {prescription.name}
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger className="bg-transparent ms-2">
                                    <div className="w-[30px]">
                                      <FontAwesomeIcon
                                        title={t("actions")}
                                        style={{ paddingTop: "4px" }}
                                        icon={solid("ellipsis-vertical")}
                                      />
                                    </div>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem
                                      className="cursor-pointer"
                                      onClick={() =>
                                        viewPrescription(index, false)
                                      }
                                    >
                                      <div className="flex">
                                        <FontAwesomeIcon
                                          title={t("view")}
                                          icon={solid("eye")}
                                          className="ml-2 mr-2 mt-1 w-[15px]"
                                        />
                                        <span>{t("view")}</span>
                                      </div>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="cursor-pointer"
                                      onClick={() => copyPrescription(index)}
                                    >
                                      <div className="flex">
                                        <FontAwesomeIcon
                                          title={t("copy")}
                                          icon={solid("copy")}
                                          className="ml-2 mr-2 mt-1 w-[15px]"
                                        />
                                        <span>{t("copy")}</span>
                                      </div>
                                    </DropdownMenuItem>
                                    {!!prescription.valid ? (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() =>
                                            runOtherPrescription(index)
                                          }
                                        >
                                          <div className="flex">
                                            <FontAwesomeIcon
                                              title={t("run")}
                                              icon={solid("play")}
                                              className="ml-2 mr-2 mt-1 w-[15px]"
                                            />
                                            <span>{t("run")}</span>
                                          </div>
                                        </DropdownMenuItem>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[400px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[40px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent>*/}
              {/* <TabsContent value="My Engagements">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("activity_name")}</TableHead>
                    <TableHead>{t("expert")}</TableHead>
                    <TableHead>
                      {t("status")}{" "}
                      <select
                        className="filterselect"
                        value={filterown}
                        onChange={(e) => setFilterown(e.target.value)}
                      >
                        <option value="ALL">{t("ALL")}</option>
                        <option value="RUNNING">{t("RUNNING")}</option>
                      </select>
                    </TableHead>
                    <TableHead>{t("last_update")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!filteredProcesses
                    ? filteredProcesses.map((process: any, index: any) => {
                        return (
                          <TableRow key={index}>
                            <TableCell>{process.prescriptionName}</TableCell>
                            <TableCell>
                              {process.fiExpert?.email}&nbsp;
                              {process.fiExpert &&
                              process.fiExpert.email !== auth.email ? (
                                <span
                                  className={
                                    "smallicon " +
                                    (process.onlineState === "offline"
                                      ? "offline"
                                      : process.onlineState === "online"
                                      ? "online"
                                      : "busy")
                                  }
                                >
                                  <FontAwesomeIcon
                                    title={t("onlinestate")}
                                    icon={solid("circle")}
                                  />
                                </span>
                              ) : (
                                ""
                              )}
                            </TableCell>
                            <TableCell>{t(process.status)}</TableCell>
                            <TableCell>
                              {showLocale(process.lastUpdated)}
                            </TableCell>
                            <TableCell>
                              {process.status === "RUNNING" ||
                              process.logCount > 0 ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger className="bg-transparent ms-2">
                                    <div className="w-[30px]">
                                      <FontAwesomeIcon
                                        title={t("actions")}
                                        style={{ paddingTop: "4px" }}
                                        icon={solid("ellipsis-vertical")}
                                      />
                                    </div>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    {process.status === "RUNNING" ? (
                                      <>
                                        {" "}
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() => continueProcess(index)}
                                        >
                                          <div className="flex">
                                            <FontAwesomeIcon
                                              title={t("continue")}
                                              icon={solid("forward")}
                                              className="ml-2 mr-2 mt-1 w-[15px]"
                                            />
                                            <span>{t("continue")}</span>
                                          </div>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() =>
                                            terminateProcess(index)
                                          }
                                        >
                                          <div className="flex">
                                            <FontAwesomeIcon
                                              title={t("terminate")}
                                              icon={solid("circle-xmark")}
                                              className="ml-2 mr-2 mt-1 w-[15px]"
                                            />
                                            <span>{t("terminate")}</span>
                                          </div>
                                        </DropdownMenuItem>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                    {process.logCount > 0 ? (
                                      <>
                                        {process.status === "RUNNING" ? (
                                          <DropdownMenuSeparator />
                                        ) : (
                                          <></>
                                        )}
                                        <DropdownMenuItem
                                          className="cursor-pointer"
                                          onClick={() => showUserLog(index)}
                                        >
                                          <div className="flex">
                                            <FontAwesomeIcon
                                              title={t("review")}
                                              icon={solid("file-lines")}
                                              className="ml-2 mr-2 mt-1 w-[15px]"
                                            />
                                            <span>{t("review")}</span>
                                          </div>
                                        </DropdownMenuItem>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <></>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[200px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[20px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent> */}
              {/* <TabsContent value="Engagements For Activities">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("activity_name")}</TableHead>
                    <TableHead>{t("user")}</TableHead>
                    <TableHead>
                      {t("status")}
                      <select
                        className="filterselect"
                        value={filterexpert}
                        onChange={(e) => setFilterexpert(e.target.value)}
                      >
                        <option value="ALL">{t("ALL")}</option>
                        <option value="RUNNING">{t("RUNNING")}</option>
                      </select>
                    </TableHead>
                    <TableHead>{t("last_update")}</TableHead>
                    <TableHead>&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!!filteredExpertProcesses
                    ? filteredExpertProcesses.map(
                        (process: any, index: any) => {
                          return (
                            <TableRow key={index}>
                              <TableCell>{process.prescriptionName}</TableCell>
                              <TableCell>
                                {process.fiInitiator?.email}&nbsp;
                                {process.fiInitiator &&
                                process.fiInitiator.email !== auth.email ? (
                                  <span
                                    className={
                                      "smallicon " +
                                      (process.onlineState === "offline"
                                        ? "offline"
                                        : process.onlineState === "online"
                                        ? "online"
                                        : "busy")
                                    }
                                  >
                                    <FontAwesomeIcon
                                      title={t("onlinestate")}
                                      icon={solid("circle")}
                                    />
                                  </span>
                                ) : (
                                  ""
                                )}
                              </TableCell>
                              <TableCell>{t(process.status)}</TableCell>
                              <TableCell>
                                {showLocale(process.lastUpdated)}
                              </TableCell>
                              <TableCell>
                                {!process.fiInitiator ||
                                process.logCount > 0 ? (
                                  <Menubar>
                                    <MenubarMenu>
                                      <MenubarTrigger className="bg-transparent cursor-pointer w-[30px]">
                                        <FontAwesomeIcon
                                          title={t("actions")}
                                          style={{ paddingTop: "4px" }}
                                          icon={solid("ellipsis-vertical")}
                                        />
                                      </MenubarTrigger>
                                      <MenubarContent>
                                        {process.logCount > 0 ? (
                                          <MenubarItem
                                            className="cursor-pointer"
                                            onClick={() => showFullLog(index)}
                                          >
                                            <div className="flex">
                                              <FontAwesomeIcon
                                                title={t("review")}
                                                icon={solid("file-lines")}
                                                className="ml-2 mr-2 mt-1 w-[15px]"
                                              />
                                              <span>{t("review")}</span>
                                            </div>
                                          </MenubarItem>
                                        ) : (
                                          <></>
                                        )}
                                      </MenubarContent>
                                    </MenubarMenu>
                                  </Menubar>
                                ) : (
                                  <></>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    : Array(Math.floor(Math.random() * 10 + 5))
                        .join(".")
                        .split(".")
                        .map((_, arrIdx) => {
                          return (
                            <TableRow key={arrIdx}>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[200px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[50px] rounded-xl bg-gray-200" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-[20px] w-[20px] rounded-xl bg-gray-200" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                </TableBody>
              </Table>
            </TabsContent> */}

              {(isMentor || isOrgAdmin) &&
                mentorCollectionCount !== null &&
                mentorCollectionCount > 0 && (
                  <TabsContent
                    value="my_groups"
                    className="flex justify-center mt-0"
                  >
                    <MyGroups />
                  </TabsContent>
                )}

          </div>
        </Tabs>
      )}
      <Dialog open={showTerminate} onOpenChange={setShowTerminate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("terminate_engagement")}</DialogTitle>
          </DialogHeader>
          <p>{t("question_terminate")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={async () => {
                Logger.debug("Current engagement: " + currentProcess);
                let response = await retryFetch(
                  `${config.API_BASE}/api/processes/${currentProcess}`,
                  {
                    method: "DELETE",
                    credentials: "include",
                  },
                );
                let msg = await response.json();
                toast.success(t(msg.message));
                setCurrentProcess("");
                setShowTerminate(false);
                setListRefresh(true);
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowTerminate(false);
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showArchive} onOpenChange={setShowArchive}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("archive_activity")}</DialogTitle>
          </DialogHeader>
          <p>{t("question_archive_activity")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={async () => {
                let response = await retryFetch(
                  `${config.API_BASE}/api/prescriptions/${currentPrescription}`,
                  {
                    method: "DELETE",
                    credentials: "include",
                  },
                );
                let msg = await response.json();
                toast.success(t(msg.message));
                setCurrentPrescription("");
                setShowArchive(false);
                setListRefresh(true);
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowArchive(false);
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("delete_activity")}</DialogTitle>
          </DialogHeader>
          <p>{t("question_delete_activity")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={async () => {
                let response = await retryFetch(
                  `${config.API_BASE}/api/prescriptions/${currentPrescription}`,
                  {
                    method: "DELETE",
                    credentials: "include",
                  },
                );
                let msg = await response.json();
                toast.success(t(msg.message));
                setCurrentPrescription("");
                setShowDelete(false);
                setListRefresh(true);
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowDelete(false);
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showRename} onOpenChange={setShowRename}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("msg_rename_activity_to")}</DialogTitle>
          </DialogHeader>
          <label htmlFor="newName">{t("name")}</label>
          <input
            className="pt-1 pb-1 pl-2 border-2 rounded"
            type="text"
            value={presNameNew}
            onChange={(e) => setPresNameNew(e.target.value)}
            id="newName"
          />
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={async () => {
                await retryFetch(
                  `${config.API_BASE}/api/prescriptions/rename/${reference}/${presNameNew}`,
                  {
                    method: "PUT",
                    credentials: "include",
                  },
                );
                setReference(null);
                setShowRename(false);
                setPresNameNew("");
                setListRefresh(true);
                // toast.success(t("msg_activity_renamed")); TODO: fix when intrdoucing react query
              }}
              data-id={"ok"}
              key={0}
              variant={`default`}
              disabled={!presNameNew}
            >
              {t("ok")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowRename(false);
                setPresNameNew("");
              }}
              data-id={"cancel"}
              key={1}
              variant={`outline`}
            >
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {messageClient && (
        <MessageModal
          open={!!messageClient}
          clientName={messageClient.clientName}
          onClose={() => setMessageClient(null)}
        />
      )}
      <WorkflowPermissionDialog
        open={showPermissionDialog}
        onOpenChange={setShowPermissionDialog}
        workflowId={currentPrescription}
      ></WorkflowPermissionDialog>
      <ExpertShareWorkflowDialog
        open={showExpertShareWorkflowDialog}
        onOpenChange={setShowExpertShareWorkflowDialog}
        workflowId={currentPrescription}
      />
      {background && <Preview />}
      <EngagementTaggingDialog
        isOpen={showRunTaggingDialog}
        onConfirm={handleRunTaggingConfirm}
        onClose={() => {
          setShowRunTaggingDialog(false);
          pendingRunPrescriptionId.current = null;
          pendingRunPrescriptionName.current = null;
        }}
        activityName={pendingRunPrescriptionName.current ?? undefined}
        confirmLabel={t("start_activity") || "Start Activity"}
        showEngagementNameField={true}
        showMentorOptions={isMentor}
      />
    </div>
  );
};

export default Portal;
